import React, { PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import LibraryBooks from '@mui/icons-material/LibraryBooks';
import CreateComponentIcon from '@mui/icons-material/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
  useSidebarOpenState,
  Link,
  GroupIcon,
} from '@backstage/core-components';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import CategoryIcon from '@mui/icons-material/Category';
import LayersIcon from '@mui/icons-material/Layers';
import TodayIcon from '@mui/icons-material/Today';
import { HelpIcon } from '@backstage/core-components';
import CheckerIcon from '@mui/icons-material/CheckCircle';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FlagIcon from '@mui/icons-material/Flag';
import { CompactSidebarPage } from './CompactSidebarPage';

const LogoRoot = styled('div')({
  width: sidebarConfig.drawerWidthClosed,
  height: 3 * sidebarConfig.logoHeight,
  display: 'flex',
  flexFlow: 'row nowrap',
  alignItems: 'center',
  marginBottom: -14,
});

// `&&` doubles selector specificity so we beat `.MuiTypography-root { margin: 0 }`
// which would otherwise win on source-order with equal specificity in the
// v5/v4 mixed stylesheet.
const LogoLink = styled(Link)({
  '&&': {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const { isOpen } = useSidebarOpenState();

  return (
    <LogoRoot>
      <LogoLink to="/" underline="none" aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </LogoLink>
    </LogoRoot>
  );
};

// Routes that should render without the global Backstage chrome (sidebar
// nav, app bar). The print views are the canonical case — they render to
// paper, so the sidebar and theme chrome would just be wasted ink.
const CHROMELESS_PATH_PREFIXES = ['/release-checklist/print/'];

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const { pathname } = useLocation();
  if (CHROMELESS_PATH_PREFIXES.some(p => pathname.startsWith(p))) {
    return <>{children}</>;
  }
  return (
    <CompactSidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="/" text="Home" />
          <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
          <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
          <SidebarItem icon={HelpIcon} to="help" text="Help" />
          {/* End global nav */}
          <SidebarDivider />
          <SidebarItem icon={TodayIcon} to="release-checklist" text="Releases" />
          <SidebarItem
            icon={CheckerIcon}
            to="version-dashboard"
            text="Version Checker"
          />
          <SidebarItem icon={AssessmentIcon} to="scorecards" text="Scorecards" />
          <SidebarItem icon={FlagIcon} to="campaigns" text="Campaigns" />
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <MyGroupsSidebarItem
          icon={GroupIcon}
          singularTitle="Team"
          pluralTitle={'Teams'}
        />
        <SidebarItem icon={LayersIcon} to="explore" text="Organization" />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </CompactSidebarPage>
  );
};
